import { AppDispatcher, DispatcherBaseAction } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { DialogActions, NewSceneCancelError } from './DialogActions';
import { SaveType, Overlay } from '../constants/Enums';
import { SceneActions } from './SceneActions';
import { ActionHelper, ActionTimeoutError } from '../utils/ActionHelper';
import { VisibilityActions } from './VisibilityActions';
import { UIHelper } from '../utils/UIHelper';
import { PconLoginStore, PconLoginOrganization, PconLoginUser } from '../stores/PconLoginStore';
import { ViewSettingsActions } from './ViewSettingsActions';
import { SceneStore } from '../stores/SceneStore';
import { ProjectSettingsStore } from '../stores/ProjectSettingsStore';
import { PCON_LOGIN_LOGOUT_API, PCON_UPDATE_PACKAGE_URL, PCON_LOGIN_ORGANIZATION_API, PCON_LOGIN_USER_API } from '../constants/Api';
import { VisibilityStore } from '../stores/VisibilityStore';
import { UrlHelper } from '../utils/UrlHelper';

// Submodules
import { Dict } from '../submodules/utils/Types';

import utils = egr.wcf.utils;
import { UnregisterCallback, registerHandler } from '../submodules/app-api/MessageManager';

interface PconUpdatePackageGroup {
    name: string;
    provider: string;
    uuid: string;
}

interface PconUpdateGetPackageGroupResponse {
    has_update_channel: boolean;
    package_groups: Array<PconUpdatePackageGroup>;
}

interface PconLoginLogoutResponse {
    result: string;
}

export abstract class PconLoginActions {
    public static async login(): Promise<void> {
        try {
            // first: save project / inform about data loss
            const saveType: SaveType = await DialogActions.newProject();
            const wasSaved: boolean = await SceneActions.saveScene(saveType);
            if (wasSaved !== true) {
                throw new NewSceneCancelError('Save action was cancelled');
            }

            // than: start (and not await) promise for package groups
            const pConUpdateResponse: Promise<DispatcherBaseAction> = ActionHelper.waitFor({
                actionTypes: [ActionConstants.UPDATE_PACKAGE_GROUPS]
            });

            // register event handler
            const unregister: UnregisterCallback = registerHandler({
                type: 'oauth_token',
                callback: (event: MessageEvent): void => {
                    PconLoginActions.updateToken(event.data.value);
                }
            });

            // than: show pCon.login site
            VisibilityActions.changeOverlay(Overlay.LOGIN);

            // wait until the login overlay was closed
            await UIHelper.waitUntilOverlayChangesInto(Overlay.NONE);
            unregister();

            // Note: the login state changes before the overlay is reset
            if (!PconLoginStore.isLoggedIn()) {
                throw new NewSceneCancelError();
            }

            // lock sreen until the package groups are available
            await ViewSettingsActions.withLock<DispatcherBaseAction>((): Promise<DispatcherBaseAction> => {
                // wait up to 5 seconds for the package groups
                const timeout: number = 5000;
                return UIHelper.rejectAfter<DispatcherBaseAction>(
                    (): Promise<DispatcherBaseAction> => {
                        return pConUpdateResponse;
                    },
                    timeout,
                    new NewSceneCancelError('Package group timeout') // FixMe: show error message
                );
            });

            // finally: open new scene
            await SceneActions.newScene(null, SaveType.NOT);
        } catch (error) {
            if (!(error instanceof NewSceneCancelError)) {
                throw error;
            }
        }
    }

    public static async logout(): Promise<void> {
        let saveType: SaveType | null = SaveType.NOT;
        if (SceneStore.getState().unsavedChanges) {
            const downloadPossible: boolean = (
                SceneStore.getState().elementCount > 0 || ProjectSettingsStore.getState().floorPlan != null
            );
            await DialogActions.newProject(!downloadPossible).then(
                (result: SaveType) => { saveType = result; }
            ).catch(
                () => { saveType = null; }
            );
        }
        if (saveType == null) {
            return;
        }
        if (saveType !== SaveType.NOT) {
            await SceneActions.closeScene(saveType);
        }

        try {
            await PconLoginStore.apiRequest<PconLoginLogoutResponse>(PCON_LOGIN_LOGOUT_API, {}, 'PUT', true);
        } catch (error) {
            // ignore result and errors
        }
        AppDispatcher.dispatch({
            actionType: ActionConstants.PCON_LOGIN_LOGOUT
        });
        await SceneActions.newScene(null, SaveType.NOT);
    }

    public static updateToken(queryString: string): void {
        if (utils.string.isNullOrEmpty(queryString)) {
            // Note: we have to send the `PCON_LOGIN_LOGOUT` action in order
            // to resolve the promise started via the `backgroundLogin` helper
            AppDispatcher.dispatch({
                actionType: ActionConstants.PCON_LOGIN_LOGOUT
            });

            PconLoginActions.updateOverlay();
            return;
        }

        const data: {access_token?: string, token_type?: string} = UrlHelper.parseQueryString(queryString);
        if (utils.string.isNullOrEmpty(data.access_token) || utils.string.isNullOrEmpty(data.token_type)) {
            throw new Error('Invalid outh query string');
        }

        if (!PconLoginStore.isLoggedIn()) {
            // FixMe: should be (re)moved later on
            setTimeout(
                () => {
                    PconLoginActions.updateUserInformation().then(PconLoginActions.updateUserOrganization);
                    const packageUpdatePromise: Promise<void> = PconLoginActions.updatePackageGroups().then(
                        async (): Promise<void> => {
                            PconLoginActions.updateOverlay();
                        }
                    );

                    if (VisibilityStore.getState().overlay === Overlay.LOGIN) {
                        ViewSettingsActions.withLock((): Promise<void> => {
                            return packageUpdatePromise;
                        });
                    }
                },
                0
            );
        }

        AppDispatcher.dispatch({
            actionType: ActionConstants.SET_PCON_LOGIN_TOKEN,
            value: data
        });
    }

    public static usePackageGroups(value: boolean): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.HIDE_PACKAGE_GROUPS,
            value: !value
        });
    }

    public static async backgroundLogin(): Promise<void> {
        const timeout: number = 5000;

        // first wait for the access token update
        const pConLoginResponse: Promise<DispatcherBaseAction> = ActionHelper.waitFor({
            actionTypes: [ActionConstants.SET_PCON_LOGIN_TOKEN, ActionConstants.PCON_LOGIN_LOGOUT],
            timeout: timeout,
            rejectError: new ActionTimeoutError('pCon.login token timeout')
        });

        const pConUpdateResponse: Promise<DispatcherBaseAction> = ActionHelper.waitFor({
            actionTypes: [ActionConstants.UPDATE_PACKAGE_GROUPS],
            timeout: timeout * 2,
            rejectError: new ActionTimeoutError('pCon.update token timeout')
        });

        // register event handler
        const unregister: UnregisterCallback = registerHandler({
            type: 'oauth_token',
            callback: (event: MessageEvent): void => {
                PconLoginActions.updateToken(event.data.value);
            }
        });

        // trigger the background login next
        UIHelper.insertBackgroundFrame(
            PconLoginStore.getAuthUrl(true),
            timeout
        );

        try {
            await pConLoginResponse;
        } catch (error) {
            if (!(error instanceof ActionTimeoutError)) {
                throw error;
            }
        } finally {
            unregister();
        }

        if (PconLoginStore.isLoggedIn()) {
            await pConUpdateResponse;
        } else {
            // do not await!
            pConUpdateResponse.catch((error: Error): void => {
                /* ignore timeout error */
                if (!(error instanceof ActionTimeoutError)) {
                    throw error;
                }
            });
        }
    }

    public static updateCache(data: Dict<unknown>): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.UPDATE_PCON_LOGIN_CACHE,
            value: data
        });
    }

    protected static updateOverlay(): void {
        if (VisibilityStore.getState().overlay === Overlay.LOGIN) {
            VisibilityActions.changeOverlay(Overlay.NONE);
        }
    }

    protected static updatePackageGroups(): Promise<void> {
        return PconLoginStore.apiRequest<PconUpdateGetPackageGroupResponse>(PCON_UPDATE_PACKAGE_URL).then((data: PconUpdateGetPackageGroupResponse): void => {
            AppDispatcher.dispatch({
                actionType: ActionConstants.UPDATE_PACKAGE_GROUPS,
                value: data.package_groups.map((value: PconUpdatePackageGroup): string => {
                    return value.uuid;
                })
            });
        });
    }

    protected static updateUserOrganization(): Promise<void> {
        return PconLoginStore.apiRequest<PconLoginOrganization>(PCON_LOGIN_ORGANIZATION_API).then((organization: PconLoginOrganization): void => {
            AppDispatcher.dispatch({
                actionType: ActionConstants.SET_PCON_LOGIN_ORGANIZATION,
                value: organization
            });
        });
    }

    protected static updateUserInformation(): Promise<void> {
        return PconLoginStore.apiRequest<PconLoginUser>(PCON_LOGIN_USER_API).then((user: PconLoginUser): void => {
            AppDispatcher.dispatch({
                actionType: ActionConstants.SET_PCON_LOGIN_USER,
                value: user
            });
        });
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/PconLoginActions.ts